import { Copyright } from "lucide-react";
import { LegalCompanyInfo } from "@/components/LegalCompanyInfo";
import { useTosVersion } from "@/components/TermsBody";
import { useReleaseDateLabel } from "@/hooks/useReleaseMetadata";

/**
 * Compliance Roadmap Item 17 — DMCA / Copyright takedown process.
 *
 * Placeholder-driven contact block for the registered DMCA agent + the
 * required-elements list from 17 U.S.C. § 512(c)(3). Nothing here is
 * invented — every operator-specific value is a mustache placeholder.
 */
function S({ n, title, id, children }) {
  return (
    <section id={id} className="space-y-2.5">
      <h2 className="font-display text-lg text-white tracking-[0.04em] flex gap-2.5">
        <span className="text-amber-300 font-mono">{n}.</span> {title}
      </h2>
      <div className="text-white/65 text-[13.5px] leading-relaxed space-y-2.5">{children}</div>
    </section>
  );
}

const Em = ({ children }) => <span className="text-white font-medium">{children}</span>;
const Placeholder = ({ children }) => (
  <span className="font-mono text-amber-200/90 bg-amber-500/[0.06] border border-amber-400/20 rounded px-1.5 py-0.5 text-[12px]">{children}</span>
);

export default function DmcaPage() {
  const version = useTosVersion();
  const releaseDate = useReleaseDateLabel();
  return (
    <div className="max-w-4xl mx-auto px-4 sm:px-6 py-12" data-testid="dmca-page">
      <div className="hud-pill rounded-2xl p-6 sm:p-10">
        <div className="flex items-center gap-3 mb-2">
          <div className="h-11 w-11 rounded-xl bg-amber-400/15 border border-amber-300/30 flex items-center justify-center shrink-0">
            <Copyright className="h-5 w-5 text-amber-300" />
          </div>
          <div>
            <div className="font-mono text-[10px] uppercase tracking-[0.35em] text-amber-300/80">Legal · DMCA / Copyright</div>
            <h1 className="font-display text-3xl sm:text-4xl text-white tracking-[0.05em]">COPYRIGHT &amp; DMCA POLICY</h1>
          </div>
        </div>
        <p data-testid="dmca-current-version" className="font-mono text-[11px] uppercase tracking-[0.25em] text-white/40 mb-8">
          Playden Inc. · Version {version} · {releaseDate}
        </p>

        <div className="space-y-8" data-testid="dmca-document">
          <div className="rounded-xl border border-amber-400/30 bg-amber-500/[0.05] p-4 text-[13px] leading-relaxed text-amber-100/90">
            <div className="font-mono text-[10px] uppercase tracking-[0.28em] text-amber-300/90 mb-1">Notice</div>
            Playden respects intellectual-property rights and responds to properly submitted takedown notices under the Digital Millennium Copyright Act (<Em>17 U.S.C. § 512</Em>) and comparable law. Playden is a &quot;service provider&quot; under the DMCA for the purpose of storing User Content at the direction of its users.
          </div>

          <S n="1" title="Registered DMCA agent" id="agent">
            <p>Notices under 17 U.S.C. § 512(c)(3) must be sent to:</p>
            <div className="rounded-lg border border-white/10 bg-[hsl(220_18%_10%)] p-4 font-mono text-[13px] text-white/85 whitespace-pre-line">
              {"Attn: DMCA Agent — "}<Placeholder>{"{{DMCA_AGENT_NAME}}"}</Placeholder>{"\n"}
              <Placeholder>{"{{LEGAL_ENTITY_NAME}}"}</Placeholder>{"\n"}
              <Placeholder>{"{{DMCA_ADDRESS}}"}</Placeholder>{"\n"}
              {"Email: "}<Placeholder>{"{{DMCA_EMAIL}}"}</Placeholder>
            </div>
            <p className="text-[12px] text-white/45">The Operator will register the agent above with the U.S. Copyright Office and keep the address current.</p>
          </S>

          <S n="2" title="Elements of a valid takedown notice" id="notice">
            <p>Your notice must be in writing and include all of the following:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>A physical or electronic signature of the copyright owner or a person authorized to act on their behalf.</li>
              <li>Identification of the copyrighted work claimed to have been infringed (or a representative list if multiple works).</li>
              <li>Identification of the material that is claimed to be infringing and information reasonably sufficient to locate it on the Service (URL, card ID, mint ID, screenshot).</li>
              <li>Your contact information: name, mailing address, telephone number, and email address.</li>
              <li>A statement that you have a good-faith belief that use of the material in the manner complained of is not authorized by the copyright owner, its agent, or the law.</li>
              <li>A statement, under penalty of perjury, that the information in the notification is accurate and that you are authorized to act on behalf of the owner of an exclusive right that is allegedly infringed.</li>
            </ul>
            <p>Notices that do not substantially comply with these requirements may not receive a response.</p>
          </S>

          <S n="3" title="Counter-notice" id="counter">
            <p>If your content was removed and you believe the removal was a mistake or that you have the right to post the material, you may submit a counter-notice under 17 U.S.C. § 512(g). Your counter-notice must include:</p>
            <ul className="list-disc pl-5 space-y-1.5">
              <li>Your physical or electronic signature.</li>
              <li>Identification of the material that was removed and its former location.</li>
              <li>A statement, under penalty of perjury, that you have a good-faith belief that the material was removed as a result of mistake or misidentification.</li>
              <li>Your name, address, and phone number; and a statement consenting to the jurisdiction of the federal district court for the district in which you reside (or, if outside the U.S., the district in which the Operator resides) and that you will accept service of process from the person who provided the original takedown notice.</li>
            </ul>
            <p>Upon receipt of a compliant counter-notice, Playden will forward it to the original complainant and may restore the removed material after the statutory waiting period unless the complainant files an action seeking a court order.</p>
          </S>

          <S n="4" title="Repeat-infringer policy" id="repeat">
            <p>Playden will terminate the accounts of users who are determined, in appropriate circumstances, to be repeat infringers. Ancillary rights to Virtual Items on terminated accounts are governed by Terms §14 (forfeiture without compensation).</p>
          </S>

          <S n="5" title="Misrepresentation" id="misrep">
            <p>Under 17 U.S.C. § 512(f), any person who knowingly materially misrepresents that material is infringing, or that material was removed by mistake, may be liable for damages. Do not submit false notices.</p>
          </S>

          <S n="6" title="Trademarks &amp; other IP" id="other-ip">
            <p>For non-copyright IP claims (trademark, right of publicity, patent), please contact <Placeholder>{"{{LEGAL_EMAIL}}"}</Placeholder> with the same level of detail and identification required for a copyright notice.</p>
          </S>

          <LegalCompanyInfo className="mt-4" testid="dmca-company-info" />
        </div>
      </div>
    </div>
  );
}
